'use client'

// What people did in the laundrette, there to see (Sam, 3 Nov: "the actions to visitors, becoming real things that
// they see happen in the world - so if for example there is an action to leave a red sock somewhere - then this
// actually happens"). Laundrette.jsx asks the town which of the place's actions were done in the last day and hands
// the ids here; lib/world/laundretteThings.js says what each one leaves and where, in the pack's own units, and the
// group below stands them in Sam's building (LAUNDRETTE_ROOT).
//
// Built small: every block and coin in one mesh with its colours on the corners, every picture (the smiley in the
// steam, the notes, the stars) in a second on one sheet drawn here once, and the drums that go round a third, so it is
// three draws whatever was done. The drums are the washers going round at the moment (lib/world/laundretteMachines.js,
// the same clock the town sends him to watch one by) and any a visitor set going, with washing tumbling in them. Rebuilt only when what is shown changes. The few things the pack has no model of
// (the radiator, the plastic chair, the corkboard, the whiteboard, the change machine, the lost property shelf, the
// dials, the puddle) are always there, in the state the actions find them in.

import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { ditherMaterial } from '../effects/ditherRegistry'
import { LAUNDRETTE_ROOT } from '../../../lib/world/laundrette'
import { LAUNDRETTE_THINGS, FIXTURES, DIALS, DECALS, MACHINES, DRYER, SETS } from '../../../lib/world/laundretteThings'
import { WASHERS, loadOf } from '../../../lib/world/laundretteMachines'

const D = Math.PI / 180
const CELL = 128
const GRID = 4
// the pack's (x, y, z up) in three's (x, y up, z)
const at3 = ([x, y, z]) => [x, z, -y]
const dithered = (m) => { ditherMaterial(m); return m }

function coloured(g, hex) {
  const c = new THREE.Color(hex)
  const n = g.attributes.position.count
  const a = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) { a[i * 3] = c.r; a[i * 3 + 1] = c.g; a[i * 3 + 2] = c.b }
  g.setAttribute('color', new THREE.BufferAttribute(a, 3))
  return g
}

// a flat thing built facing three's +z (the pack's -y), turned to face the way it is meant to
function face(g, f) {
  if (f === '+y') g.rotateY(Math.PI)
  else if (f === '+x') g.rotateY(Math.PI / 2)
  else if (f === '-x') g.rotateY(-Math.PI / 2)
  else if (f === 'up') g.rotateX(-Math.PI / 2)
  return g
}

// stood where it goes; `on` is a fixture whose place and turn it shares (the cards on the chair)
function place(g, at, frame) {
  if (frame) {
    g.rotateY((frame.yaw || 0) * D)
    const [x, y, z] = at3([frame.at[0], frame.at[1], 0])
    g.translate(x, y, z)
  }
  return g
}

function solidOf(p, frame) {
  let g
  if (p.box) {
    const [w, d, h] = p.box
    g = new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0)
  } else if (p.cyl) {
    const [r, h] = p.cyl
    g = new THREE.CylinderGeometry(r, r, h, 10).translate(0, h / 2, 0)
  } else if (p.ball) {
    g = new THREE.SphereGeometry(p.ball, 8, 6)
  } else if (p.still) {
    g = face(new THREE.CircleGeometry(0.27, 10), '-y')
    return coloured(g.translate(...at3([MACHINES[p.still], 6.2, 0.625])), p.c)
  } else return null
  if (p.rx) g.rotateX(p.rx * D)
  if (p.rz) g.rotateZ(p.rz * D)
  if (p.yaw) g.rotateY(p.yaw * D)
  g.translate(...at3(p.at))
  return coloured(place(g, null, frame), p.c)
}

function decalOf(p, frame) {
  const g = new THREE.PlaneGeometry(p.size[0], p.size[1])
  const i = DECALS.indexOf(p.decal)
  const col = i % GRID
  const row = Math.floor(i / GRID)
  const uv = g.attributes.uv
  for (let k = 0; k < uv.count; k++) uv.setXY(k, (col + uv.getX(k)) / GRID, 1 - (row + 1 - uv.getY(k)) / GRID)
  if (p.spin) g.rotateZ(p.spin * D)
  face(g, p.face)
  g.translate(...at3(p.at))
  return place(g, null, frame)
}

// the always-there things, as parts, in the state the live actions leave them
function fixtures(live) {
  const on = (name) => live.has(SETS[name])
  const parts = []
  const add = (...ps) => parts.push(...ps)

  const rad = FIXTURES.radiator.at
  add({ box: [0.55, 0.1, 0.55], at: rad, c: '#d8d3c6' })
  for (let i = 0; i < 5; i++) add({ box: [0.03, 0.115, 0.5], at: [rad[0] - 0.22 + i * 0.11, rad[1], rad[2] + 0.025], c: '#c4bdac' })

  const chair = on('chair') ? FIXTURES.chair.turned : FIXTURES.chair
  const blue = '#3f7fb5'
  const sat = { at: chair.at, yaw: chair.yaw }
  add({ box: [0.42, 0.42, 0.04], at: [0, 0, 0.42], c: blue, on: sat })
  add({ box: [0.04, 0.42, 0.4], at: [0.19, 0, 0.46], c: blue, on: sat })
  for (const [x, y] of [[-0.18, -0.18], [-0.18, 0.18], [0.18, -0.18], [0.18, 0.18]]) add({ box: [0.03, 0.03, 0.42], at: [x, y, 0], c: '#2d5f8a', on: sat })

  const cb = FIXTURES.corkboard
  add({ box: [cb.size[0] + 0.06, 0.02, cb.size[1] + 0.06], at: [cb.at[0], cb.at[1] + 0.01, cb.at[2] - 0.03], c: '#6b4a2b' })
  add({ box: [cb.size[0], 0.03, cb.size[1]], at: [cb.at[0], cb.at[1] - 0.005, cb.at[2]], c: '#c99c64' })
  const straight = on('notices')
  ;[[0.78, 2.04, -12, '#f6f3ea'], [0.98, 1.67, 9, '#f3e7a6'], [1.3, 1.67, -6, '#f1d0d8']].forEach(([x, z, tilt, c]) => {
    add({ box: [0.18, 0.006, 0.22], at: [x, cb.at[1] - 0.022, z], c, rz: straight ? 0 : tilt })
    add({ ball: 0.012, at: [x, cb.at[1] - 0.03, z + 0.2], c: '#c3282e' })
  })

  const wb = FIXTURES.whiteboard
  add({ box: [0.02, wb.size[0] + 0.04, wb.size[1] + 0.04], at: [wb.at[0] + 0.012, wb.at[1], wb.at[2] - 0.02], c: '#9a9ea3' })
  add({ box: [0.025, wb.size[0], wb.size[1]], at: [wb.at[0], wb.at[1], wb.at[2]], c: '#f2f2ee' })
  add({ box: [0.06, 0.4, 0.02], at: [wb.at[0] - 0.03, wb.at[1], wb.at[2] - 0.03], c: '#9a9ea3' })

  const ch = FIXTURES.change.at
  add({ box: [0.45, 0.3, 0.8], at: ch, c: '#8d9096' })
  add({ box: [0.3, 0.01, 0.12], at: [ch[0], ch[1] - 0.155, ch[2] + 0.55], c: '#24364a' })
  add({ box: [0.06, 0.01, 0.02], at: [ch[0], ch[1] - 0.155, ch[2] + 0.4], c: '#1d1d1d' })
  add({ box: [0.2, 0.08, 0.015], at: [ch[0], ch[1] - 0.17, ch[2] + 0.08], c: '#3a3a3a' })
  add({ box: [0.2, 0.01, 0.04], at: [ch[0], ch[1] - 0.21, ch[2] + 0.08], c: '#3a3a3a' })

  const sh = FIXTURES.shelf
  add({ box: [sh.w, 0.25, 0.04], at: sh.at, c: '#8a6a45' })
  for (const dx of [-0.45, 0.45]) add({ box: [0.03, 0.2, 0.12], at: [sh.at[0] + dx, sh.at[1] + 0.02, sh.at[2] - 0.12], c: '#5e4630' })
  const rainbow = ['#c3282e', '#e07a2c', '#e3c33b', '#4f9a4b', '#3d6fb0']
  const order = on('shelf') ? [0, 1, 2, 3, 4] : [3, 0, 4, 1, 2]
  order.forEach((k, i) => add({ box: [0.15, 0.15, [0.1, 0.13, 0.08, 0.12, 0.09][k]], at: [sh.at[0] - 0.5 + i * 0.2, sh.at[1], sh.at[2] + 0.04], c: rainbow[k], yaw: on('shelf') ? 0 : [12, -9, 20, -15, 6][i] }))

  // every dial: straight up where they were left, all the way to one side when somebody turned them to the gentlest
  const left = [40, -70, 110, 15, -130, 80, -20, 150, -95, 60, -45, 125, 5, -110, 95, -160]
  DIALS.forEach((d, i) => {
    const turn = (on('dials') ? -120 : left[i]) * D
    const knob = new THREE.CylinderGeometry(0.045, 0.045, 0.025, 10).rotateX(Math.PI / 2)
    const tick = new THREE.BoxGeometry(0.014, 0.05, 0.01).translate(0, 0.022, 0.014).rotateZ(turn)
    for (const [g, c] of [[knob, '#d9dcdf'], [tick, '#2b2b2b']]) parts.push({ built: coloured(face(g, d.face).translate(...at3(d.at)), c) })
  })

  if (!on('puddle')) add({ decal: 'puddle', size: FIXTURES.puddle.size, at: FIXTURES.puddle.at, face: 'up' })
  return parts
}

// the sheet of pictures, drawn once on the page
let sheet = null
function pictures() {
  if (sheet) return sheet
  const cv = document.createElement('canvas')
  cv.width = cv.height = CELL * GRID
  const x = cv.getContext('2d')
  const hand = '"Comic Sans MS", "Chalkboard SE", cursive'
  const cell = (name, draw) => {
    const i = DECALS.indexOf(name)
    x.save()
    x.translate((i % GRID) * CELL, Math.floor(i / GRID) * CELL)
    x.beginPath(); x.rect(0, 0, CELL, CELL); x.clip()
    draw()
    x.restore()
  }
  const paper = (c, l, t, w, h) => { x.fillStyle = c; x.fillRect(l, t, w, h) }
  const lines = (ls, font, colour, top, gap) => {
    x.fillStyle = colour; x.font = font; x.textAlign = 'center'; x.textBaseline = 'middle'
    ls.forEach((s, k) => x.fillText(s, CELL / 2, top + k * gap))
  }
  const fog = (alpha) => {
    x.fillStyle = `rgba(232,238,241,${alpha})`
    for (const [cx, cy, r] of [[64, 64, 52], [40, 50, 30], [88, 46, 30], [50, 90, 30], [86, 88, 30]]) { x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.fill() }
  }

  cell('smiley', () => {
    fog(0.92)
    x.globalCompositeOperation = 'destination-out'
    x.lineWidth = 7; x.lineCap = 'round'
    x.beginPath(); x.arc(64, 64, 38, 0, Math.PI * 2); x.stroke()
    x.beginPath(); x.arc(64, 70, 20, 0.15 * Math.PI, 0.85 * Math.PI); x.stroke()
    for (const ex of [50, 78]) { x.beginPath(); x.arc(ex, 54, 5, 0, Math.PI * 2); x.fill() }
  })
  cell('stillSpinning', () => {
    x.fillStyle = 'rgba(178,170,154,0.95)'; x.fillRect(4, 24, 120, 80)
    x.fillStyle = 'rgba(150,142,128,0.95)'
    for (let k = 0; k < 160; k++) x.fillRect(4 + ((k * 37) % 118), 24 + ((k * 53) % 78), 2, 2)
    x.globalCompositeOperation = 'destination-out'
    lines(['still', 'spinning'], `bold 30px ${hand}`, '#000', 46, 36)
  })
  cell('rainNote', () => {
    paper('#f4efe2', 10, 12, 108, 104)
    lines(['your washing', 'smells like', 'rain'], `17px ${hand}`, '#2f4f8f', 42, 26)
    x.fillStyle = '#c3282e'; x.beginPath(); x.arc(64, 20, 5, 0, Math.PI * 2); x.fill()
  })
  cell('poem', () => {
    lines(['wash day', 'round and round', 'the socks go down', 'the warm air waits', 'for nobody'], `14px ${hand}`, '#244a9c', 18, 23)
  })
  cell('sockDrawing', () => {
    paper('#fbfaf5', 8, 8, 112, 112)
    x.fillStyle = '#e3a0a0'; x.strokeStyle = '#c3282e'; x.lineWidth = 4
    x.beginPath(); x.moveTo(52, 20); x.lineTo(78, 20); x.lineTo(78, 76); x.quadraticCurveTo(78, 100, 54, 100)
    x.lineTo(34, 100); x.quadraticCurveTo(24, 100, 26, 88); x.quadraticCurveTo(30, 78, 52, 76); x.closePath(); x.fill(); x.stroke()
    lines(['x'], `bold 22px ${hand}`, '#333', 108, 0)
  })
  cell('handprints', () => {
    x.fillStyle = 'rgba(240,245,248,0.9)'
    for (const [cx, flip] of [[38, -1], [90, 1]]) {
      x.beginPath(); x.ellipse(cx, 80, 17, 21, 0, 0, Math.PI * 2); x.fill()
      ;[-11, -4, 3, 10].forEach((dx, k) => { x.beginPath(); x.ellipse(cx + dx, 46 - (k === 1 || k === 2 ? 6 : 0), 4, 12, 0, 0, Math.PI * 2); x.fill() })
      x.beginPath(); x.ellipse(cx + flip * 20, 74, 5, 11, flip * 0.7, 0, Math.PI * 2); x.fill()
    }
  })
  cell('footprints', () => {
    x.fillStyle = 'rgba(70,64,56,0.85)'
    for (const [cx, cy, a] of [[44, 82, -0.1], [84, 40, 0.1]]) {
      x.beginPath(); x.ellipse(cx, cy, 12, 22, a, 0, Math.PI * 2); x.fill()
      x.beginPath(); x.ellipse(cx + 1, cy + 30, 9, 9, 0, 0, Math.PI * 2); x.fill()
    }
  })
  cell('puddle', () => {
    x.fillStyle = 'rgba(105,128,150,0.85)'; x.beginPath(); x.ellipse(64, 64, 60, 48, 0.2, 0, Math.PI * 2); x.fill()
    x.beginPath(); x.ellipse(30, 92, 20, 14, 0, 0, Math.PI * 2); x.fill()
    x.fillStyle = 'rgba(214,226,234,0.9)'; x.beginPath(); x.ellipse(74, 46, 26, 6, 0.3, 0, Math.PI * 2); x.fill()
  })
  cell('star', () => {
    x.fillStyle = '#e8b923'; x.strokeStyle = '#8a6a10'; x.lineWidth = 5
    x.beginPath()
    for (let k = 0; k < 10; k++) {
      const r = k % 2 ? 24 : 56
      const a = -Math.PI / 2 + k * Math.PI / 5
      x.lineTo(64 + r * Math.cos(a), 66 + r * Math.sin(a))
    }
    x.closePath(); x.fill(); x.stroke()
  })
  cell('lostSock', () => {
    paper('#fffef6', 4, 16, 120, 96)
    x.fillStyle = '#c3282e'; x.fillRect(10, 42, 108, 2)
    lines(['LOST:'], `bold 20px ${hand}`, '#222', 30, 0)
    lines(['one sock', 'sentimental value', 'no questions'], `13px ${hand}`, '#333', 60, 18)
  })
  cell('shortWash', () => {
    paper('#fbf7ea', 6, 10, 116, 108)
    x.fillStyle = '#b0b4b9'; x.beginPath(); x.arc(64, 34, 16, 0, Math.PI * 2); x.fill()
    lines(['20'], 'bold 12px sans-serif', '#555', 34, 0)
    x.fillStyle = 'rgba(232,226,190,0.95)'; x.fillRect(36, 28, 56, 12)
    lines(['for a short wash', 'small emergencies', 'only'], `13px ${hand}`, '#333', 68, 18)
  })
  cell('washMe', () => {
    paper('#fff1a8', 6, 18, 116, 92)
    lines(['wash me'], `bold 28px ${hand}`, '#1f1f1f', 64, 0)
  })
  ;['one', 'two', 'three'].forEach((name, k) => cell(name, () => {
    paper('#222', 10, 10, 108, 108); paper('#ffffff', 16, 16, 96, 96)
    lines([String(k + 1)], 'bold 72px sans-serif', '#222', 66, 0)
  }))
  cell('rosette', () => {
    x.fillStyle = '#c3282e'
    x.beginPath(); x.moveTo(44, 70); x.lineTo(30, 124); x.lineTo(48, 112); x.lineTo(60, 124); x.lineTo(62, 70); x.fill()
    x.beginPath(); x.moveTo(66, 70); x.lineTo(68, 124); x.lineTo(80, 112); x.lineTo(98, 124); x.lineTo(84, 70); x.fill()
    x.fillStyle = '#e8b923'; x.beginPath(); x.arc(64, 50, 40, 0, Math.PI * 2); x.fill()
    x.fillStyle = '#c3282e'; x.beginPath(); x.arc(64, 50, 28, 0, Math.PI * 2); x.fill()
    lines(['1st'], 'bold 22px sans-serif', '#fff', 51, 0)
  })

  const tex = new THREE.CanvasTexture(cv)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  sheet = tex
  return sheet
}

// what is in a drum, drawn once on the page: the dark drum with its holes, and washing tumbled in it, light, so a
// machine's load can tint it (whites, darks, colours: loadOf in lib/world/laundretteMachines.js)
let washing = null
function washingPicture() {
  if (washing) return washing
  const cv = document.createElement('canvas')
  cv.width = cv.height = 128
  const x = cv.getContext('2d')
  x.fillStyle = '#3a3d44'; x.fillRect(0, 0, 128, 128)
  x.fillStyle = '#2a2c31'
  for (let r = 14; r < 64; r += 12) for (let a = 0; a < 6.28; a += 0.5) x.fillRect(64 + Math.cos(a + r) * r, 64 + Math.sin(a + r) * r, 3, 3)
  // the washing: heaped low in the drum, as it falls when it turns
  const cloth = [
    ['#f4f4f2', 30, 84, 26, 14, 0.3], ['#d9d9d6', 64, 92, 30, 16, -0.2], ['#ffffff', 96, 82, 22, 15, 0.6],
    ['#c9c9c6', 46, 70, 20, 10, -0.5], ['#e6e6e3', 82, 66, 18, 9, 0.2], ['#bdbdba', 64, 108, 26, 10, 0],
    ['#ffffff', 22, 62, 10, 18, 0.9], ['#d0d0cd', 104, 104, 14, 9, -0.7],
  ]
  for (const [c, cx, cy, rx, ry, a] of cloth) {
    x.fillStyle = c; x.beginPath(); x.ellipse(cx, cy, rx, ry, a, 0, Math.PI * 2); x.fill()
    x.strokeStyle = 'rgba(40,40,46,0.35)'; x.lineWidth = 2; x.stroke()
  }
  // a sock, the one you watch for
  x.fillStyle = '#ffffff'; x.fillRect(58, 30, 9, 26); x.fillRect(58, 50, 20, 9)
  const tex = new THREE.CanvasTexture(cv)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.magFilter = THREE.NearestFilter
  tex.minFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  washing = tex
  return washing
}

// where a drum is and which way it faces, in the pack's own units: a washer's from lib/world/laundretteMachines.js,
// or the dryer's in the wall
const FACING = { '-y': 0, '+x': Math.PI / 2, '-x': -Math.PI / 2 }
function drumAt(id) {
  if (id === 'dryer') return { at: [DRYER.x, DRYER.y, DRYER.z], turn: Math.PI / 2, r: DRYER.r }
  const w = WASHERS.find((m) => m.id === id)
  return w && { at: w.door, turn: FACING[w.face], r: 0.27 }
}

// The drums going round, one instanced mesh: the washing tumbles one way and then back, and on the spin goes round
// fast the one way
function Drums({ drums }) {
  const mesh = useRef()
  const geo = useMemo(() => new THREE.CircleGeometry(1, 12), [])
  const mat = useMemo(() => dithered(new THREE.MeshStandardNodeMaterial({ map: washingPicture(), roughness: 0.8, metalness: 0 })), [])
  const turned = useRef([])
  const clock = useRef(0)
  const tmp = useMemo(() => ({ m: new THREE.Matrix4(), q: new THREE.Quaternion(), s: new THREE.Quaternion(), p: new THREE.Vector3(), k: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0), z: new THREE.Vector3(0, 0, 1), c: new THREE.Color() }), [])
  useEffect(() => () => { geo.dispose(); mat.dispose() }, [geo, mat])
  useEffect(() => {
    const m = mesh.current
    if (!m) return
    drums.forEach((d, i) => m.setColorAt(i, tmp.c.set(d.c)))
    if (m.instanceColor) m.instanceColor.needsUpdate = true
  }, [drums, tmp])

  useFrame((_, dt) => {
    const m = mesh.current
    if (!m) return
    clock.current += dt
    let n = 0
    drums.forEach((d, i) => {
      const where = drumAt(d.id)
      if (!where) return
      // a wash turns a few seconds one way, stops, and turns back; the spin goes one way, fast
      const way = d.spin ? 1 : Math.sin(clock.current * 0.45 + i * 1.7)
      const speed = d.spin ? 14 : 2.6 * Math.sign(way) * Math.min(1, Math.abs(way) * 4)
      turned.current[i] = (turned.current[i] || i) + dt * speed
      tmp.q.setFromAxisAngle(tmp.up, where.turn)
      tmp.s.setFromAxisAngle(tmp.z, turned.current[i])
      tmp.q.multiply(tmp.s)
      m.setMatrixAt(n++, tmp.m.compose(tmp.p.set(...at3(where.at)), tmp.q, tmp.k.set(where.r, where.r, where.r)))
    })
    m.count = n
    m.instanceMatrix.needsUpdate = true
  })

  return <instancedMesh ref={mesh} args={[geo, mat, 24]} frustumCulled={false} receiveShadow />
}

export function LaundretteThings({ live, running = [] }) {
  const key = [...live].sort().join('|')
  const built = useMemo(() => {
    const set = new Set(live)
    const parts = [...fixtures(set)]
    const drums = []
    for (const id of set) {
      for (const p of LAUNDRETTE_THINGS[id] || []) {
        if (p.drum) drums.push({ id: p.drum, c: p.c, spin: false })
        else if (!p.sets) parts.push(p.on === 'chair' ? { ...p, on: set.has(SETS.chair) ? FIXTURES.chair.turned : FIXTURES.chair } : p)
      }
    }
    const solids = []
    const decals = []
    for (const p of parts) {
      if (p.built) { solids.push(p.built); continue }
      const frame = p.on ? { at: p.on.at, yaw: p.on.yaw } : null
      if (p.decal) decals.push(decalOf(p, frame))
      else { const g = solidOf(p, frame); if (g) solids.push(g) }
    }
    const solid = solids.length ? mergeGeometries(solids, false) : null
    const decal = decals.length ? mergeGeometries(decals, false) : null
    for (const g of [...solids, ...decals]) g.dispose()
    return { solid, decal, drums }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  // and the washers going round at this moment (lib/world/laundretteMachines.js), where a visitor has not set one going
  const turning = running.map((r) => `${r.id}${r.spin ? '!' : ''}`).join('|')
  const drums = useMemo(() => {
    const set = new Set(built.drums.map((d) => d.id))
    return [...built.drums, ...running.filter((r) => !set.has(r.id)).map((r) => ({ id: r.id, c: loadOf(r.id), spin: r.spin }))]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, turning])
  useEffect(() => () => { built.solid?.dispose(); built.decal?.dispose() }, [built])

  const mats = useMemo(() => {
    const solid = dithered(new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.9, metalness: 0 }))
    const decal = dithered(new THREE.MeshStandardNodeMaterial({ map: pictures(), alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.9, metalness: 0 }))
    return { solid, decal }
  }, [])
  useEffect(() => () => { mats.solid.dispose(); mats.decal.dispose() }, [mats])

  return (
    <group position={[LAUNDRETTE_ROOT.x, 0, LAUNDRETTE_ROOT.z]} rotation-y={LAUNDRETTE_ROOT.rotY} scale={LAUNDRETTE_ROOT.scale}>
      {built.solid && <mesh geometry={built.solid} material={mats.solid} receiveShadow />}
      {built.decal && <mesh geometry={built.decal} material={mats.decal} receiveShadow />}
      {drums.length > 0 && <Drums drums={drums} />}
    </group>
  )
}

export default LaundretteThings
